import { useState, type ReactNode } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  SourceCodeIcon,
  ArrowLeft02Icon,
  ArrowRight01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  usePopoverContext
} from "shared/components/Popover";
import { VariableFallbackModal } from "views/settings-communication/components/VariableFallbackModal";
import type { SmsVariable } from "views/settings-communication/api/types";
import type { ContactUnitLinks } from "../api/crm";
import { isLinkVariableKey, type LinkType } from "./link-variable-utils";
import { linkForType, unavailableLinkHint } from "./link-variable-shared";
import { useInboxEmbed } from "../context/useInboxEmbed";
import { cx } from "shared/utils/classnames";

// Fallback-required variables whose value is tied to a specific unit/
// property, so (like a link variable) they get a unit picker instead of
// going straight to free-text fallback entry. `dedupe` collapses units that
// share the same value (several units under one property share its name);
// property_address/listing_link are genuinely per-unit, so every unit is
// listed.
interface UnitScopedValuePickerConfig {
  dedupe: boolean;
  valueOf: (unit: ContactUnitLinks) => string | null;
}

const UNIT_SCOPED_VALUE_PICKERS: Record<string, UnitScopedValuePickerConfig> = {
  "lead.property_name": { dedupe: true, valueOf: (unit) => unit.property_name },
  "lead.property_address": {
    dedupe: false,
    valueOf: (unit) => unit.property_address
  },
  "lead.listing_link": { dedupe: false, valueOf: (unit) => unit.listing_link }
};

interface InboxInsertVariableMenuProps {
  variables: SmsVariable[];
  units: ContactUnitLinks[];
  // Resolved preview values for NAME variables, keyed by variable key. Used
  // both as the muted preview next to a label and as the inserted chip title.
  values: Record<string, string>;
  // Insert a name-variable chip. `value` is the resolved content (the chip
  // title that serialises into the outgoing body); `key` is the variable key.
  onInsertName: (key: string, value: string) => void;
  // Insert a link-variable chip bound to the chosen unit.
  onInsertLink: (key: LinkType, unit: ContactUnitLinks) => void;
  // Trigger element rendered inside the Popover (e.g. the "+" icon button).
  // It should carry its own `disabled` state to gate opening.
  trigger: ReactNode;
}

interface VariableListProps {
  variables: SmsVariable[];
  units: ContactUnitLinks[];
  values: Record<string, string>;
  onInsertName: (key: string, value: string) => void;
  onInsertLink: (key: LinkType, unit: ContactUnitLinks) => void;
  onBack: () => void;
  // Called instead of inserting when a fallback-required variable has no
  // resolved preview value — inserting it directly would leave a
  // permanently blank chip in the outgoing message with no way to add
  // fallback text after the fact.
  onRequiresFallback: (variable: SmsVariable) => void;
}

// The variable list level + per-link-variable unit submenu. Kept under
// <Popover> so it can close itself via usePopoverContext after a selection.
const VariableList = ({
  variables,
  units,
  values,
  onInsertName,
  onInsertLink,
  onBack,
  onRequiresFallback
}: VariableListProps) => {
  const { setOpen } = usePopoverContext();
  const [activeLink, setActiveLink] = useState<{
    key: LinkType;
    label: string;
  } | null>(null);
  const [valuePicker, setValuePicker] = useState<{
    variable: SmsVariable;
    config: UnitScopedValuePickerConfig;
  } | null>(null);
  const hasPickerOptions = (config: UnitScopedValuePickerConfig) =>
    units.some((unit) => config.valueOf(unit));

  if (activeLink)
    return (
      <div role="menu">
        <button
          type="button"
          onClick={() => setActiveLink(null)}
          className="flex w-full items-center gap-2 px-4 py-2.5 text-sm font-bold text-content-1000 hover:bg-alpha-40 border-b border-alpha-40"
        >
          <HugeiconsIcon icon={ArrowLeft02Icon} size={18} />
          {activeLink.label}
        </button>
        {units.length === 0 ? (
          <div className="px-4 py-3 text-sm font-medium text-content-300">
            No units available for this contact
          </div>
        ) : (
          units.map((unit) => {
            const link = linkForType(unit, activeLink.key);
            return (
              <button
                key={unit.unit_id}
                type="button"
                disabled={!link}
                onClick={() => {
                  onInsertLink(activeLink.key, unit);
                  setOpen(false);
                }}
                className="flex w-full flex-col items-start gap-0.5 px-4 py-2.5 text-left hover:bg-alpha-40 disabled:cursor-not-allowed disabled:opacity-50 border-t border-alpha-40 first:border-t-0"
              >
                <span className="text-sm font-medium text-content-1000">
                  {unit.label}
                </span>
                <span className="text-xs font-medium text-content-300 break-all">
                  {link
                    ? link.display_url
                    : unavailableLinkHint(unit, activeLink.key)}
                </span>
              </button>
            );
          })
        )}
      </div>
    );

  if (valuePicker) {
    const { variable, config } = valuePicker;
    // Several units can share the same value (e.g. one property name across
    // its units) — dedupe when the config says so, otherwise list every unit.
    const seen = new Set<string>();
    const options = units.filter((unit) => {
      const value = config.valueOf(unit);
      if (!value) return false;
      if (config.dedupe) {
        if (seen.has(value)) return false;
        seen.add(value);
      }
      return true;
    });
    return (
      <div role="menu">
        <button
          type="button"
          onClick={() => setValuePicker(null)}
          className="flex w-full items-center gap-2 px-4 py-2.5 text-sm font-bold text-content-1000 hover:bg-alpha-40 border-b border-alpha-40"
        >
          <HugeiconsIcon icon={ArrowLeft02Icon} size={18} />
          {variable.label}
        </button>
        {options.map((unit) => {
          const value = config.valueOf(unit) ?? "";
          return (
            <button
              key={unit.unit_id}
              type="button"
              onClick={() => {
                onInsertName(variable.key, value);
                setOpen(false);
              }}
              className={
                config.dedupe
                  ? "flex w-full items-center px-4 py-2.5 text-left text-sm font-medium text-content-1000 hover:bg-alpha-40 border-t border-alpha-40 first:border-t-0"
                  : "flex w-full flex-col items-start gap-0.5 px-4 py-2.5 text-left hover:bg-alpha-40 border-t border-alpha-40 first:border-t-0"
              }
            >
              {config.dedupe ? (
                value
              ) : (
                <>
                  <span className="text-sm font-medium text-content-1000">
                    {unit.label}
                  </span>
                  <span className="text-xs font-medium text-content-300 break-all">
                    {value}
                  </span>
                </>
              )}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            onRequiresFallback(variable);
          }}
          className="flex w-full items-center px-4 py-2.5 text-left text-sm font-medium text-content-300 hover:bg-alpha-40 border-t border-alpha-40"
        >
          Enter custom text
        </button>
      </div>
    );
  }

  return (
    <div role="menu">
      <button
        type="button"
        onClick={onBack}
        className="flex w-full items-center gap-2 px-4 py-2.5 text-sm font-bold text-content-1000 hover:bg-alpha-40 border-b border-alpha-40"
      >
        <HugeiconsIcon icon={ArrowLeft02Icon} size={18} />
        Variables
      </button>
      {variables.length === 0 ? (
        <div className="px-4 py-2.5 text-sm font-medium text-content-300">
          No variables available
        </div>
      ) : (
        variables.map((variable) => {
          const isLink = isLinkVariableKey(variable.key);
          const previewValue = values[variable.key];
          const needsFallback = variable.requires_fallback && !previewValue;
          const pickerConfig = UNIT_SCOPED_VALUE_PICKERS[variable.key];
          // True whenever this row drills into a submenu instead of
          // inserting immediately — link variables always do; a blank
          // fallback-required variable does too, but only when there's an
          // actual picker list to choose from (otherwise it opens the
          // free-text modal directly, so no chevron).
          const opensSubmenu =
            isLink ||
            (needsFallback && !!pickerConfig && hasPickerOptions(pickerConfig));
          return (
            <button
              key={variable.key}
              type="button"
              onClick={() => {
                if (isLink) {
                  const key = variable.key as LinkType;
                  // Pre-select when the contact has exactly one unit that
                  // supports this link — skip the submenu entirely.
                  const onlyUnit =
                    units.length === 1 && linkForType(units[0], key)
                      ? units[0]
                      : null;
                  if (onlyUnit) {
                    onInsertLink(key, onlyUnit);
                    setOpen(false);
                  } else
                    setActiveLink({
                      key,
                      label: variable.label
                    });
                } else if (needsFallback) {
                  // No resolved value for a variable that must never render
                  // blank. If the contact has units with a known value, let
                  // the author pick the SPECIFIC one this message is about
                  // (mirrors the link-variable unit picker) instead of
                  // guessing; only fall through to free-text fallback entry
                  // when no value is available to choose from.
                  if (pickerConfig && hasPickerOptions(pickerConfig))
                    return setValuePicker({ variable, config: pickerConfig });
                  setOpen(false);
                  onRequiresFallback(variable);
                } else {
                  // Chip title is the RESOLVED value (empty when unresolved),
                  // so the outgoing body carries literal text.
                  onInsertName(variable.key, previewValue ?? "");
                  setOpen(false);
                }
              }}
              className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm font-medium text-content-1000 hover:bg-alpha-40 border-t border-alpha-40"
            >
              <span className="flex items-center gap-1.5 min-w-0">
                <span className="shrink-0">{variable.label}</span>
                {!isLink && previewValue ? (
                  <span className="truncate text-content-300">
                    ({previewValue})
                  </span>
                ) : null}
              </span>
              {opensSubmenu ? (
                <HugeiconsIcon
                  icon={ArrowRight01Icon}
                  size={18}
                  className="text-content-300 shrink-0"
                />
              ) : null}
            </button>
          );
        })
      )}
    </div>
  );
};

interface MenuBodyProps {
  variables: SmsVariable[];
  units: ContactUnitLinks[];
  values: Record<string, string>;
  onInsertName: (key: string, value: string) => void;
  onInsertLink: (key: LinkType, unit: ContactUnitLinks) => void;
  onRequiresFallback: (variable: SmsVariable) => void;
}

// Top level of the menu (matches Figma): a single "</> Variables  >" row that
// drills into the variable list. (Upload file & photo is intentionally out of
// scope and omitted.)
const MenuBody = ({
  variables,
  units,
  values,
  onInsertName,
  onInsertLink,
  onRequiresFallback
}: MenuBodyProps) => {
  const [showVariables, setShowVariables] = useState(false);

  if (showVariables)
    return (
      <VariableList
        variables={variables}
        units={units}
        values={values}
        onInsertName={onInsertName}
        onInsertLink={onInsertLink}
        onBack={() => setShowVariables(false)}
        onRequiresFallback={onRequiresFallback}
      />
    );

  return (
    <div role="menu">
      <button
        type="button"
        onClick={() => setShowVariables(true)}
        className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm font-bold text-content-1000 hover:bg-alpha-40"
      >
        <span className="flex items-center gap-2">
          <HugeiconsIcon icon={SourceCodeIcon} size={18} />
          Variables
        </span>
        <HugeiconsIcon
          icon={ArrowRight01Icon}
          size={18}
          className="text-content-300 shrink-0"
        />
      </button>
    </div>
  );
};

export const InboxInsertVariableMenu = ({
  variables,
  units,
  values,
  onInsertName,
  onInsertLink,
  trigger
}: InboxInsertVariableMenuProps) => {
  const embed = useInboxEmbed();
  // Rendered as a sibling of <Popover> below, not nested inside
  // PopoverContent — PopoverContent unmounts on close, and the modal
  // stealing focus can trigger that close mid-open.
  const [pendingFallbackVariable, setPendingFallbackVariable] =
    useState<SmsVariable | null>(null);

  // The trigger element carries its own `disabled` state — a disabled button
  // natively swallows the click, so the popover never opens when disabled.
  return (
    <>
      <Popover placement="top-start">
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent
          className={cx(
            embed?.menuZClass ?? "z-50",
            "w-72 max-h-[340px] overflow-y-auto rounded-lg bg-content-1000-inverted shadow-lg ring-1 ring-alpha-100 overflow-clip"
          )}
        >
          <MenuBody
            variables={variables}
            units={units}
            values={values}
            onInsertName={onInsertName}
            onInsertLink={onInsertLink}
            onRequiresFallback={setPendingFallbackVariable}
          />
        </PopoverContent>
      </Popover>

      <VariableFallbackModal
        opened={pendingFallbackVariable !== null}
        mode="insert"
        label={pendingFallbackVariable?.label ?? ""}
        onConfirm={(value) => {
          if (pendingFallbackVariable)
            onInsertName(pendingFallbackVariable.key, value);
          setPendingFallbackVariable(null);
        }}
        onClose={() => setPendingFallbackVariable(null)}
      />
    </>
  );
};
